import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { RUNS } from "@/constants/testIds";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function dur(a, b) {
  if (!a || !b) return "";
  try {
    const ms = new Date(b) - new Date(a);
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${Math.round(ms/1000)}s`;
    return `${Math.round(ms/60000)}m`;
  } catch { return ""; }
}

export default function RunHistory() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/runs", { params: { limit: 50 } }); setRuns(r.data); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid={RUNS.root}>
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8B9891] mb-2">Activity</p>
        <h1 className="font-heading text-4xl tracking-tight text-[#1C2321] font-medium">Run history</h1>
        <p className="mt-2 text-[#5D6D63] max-w-2xl">Each row is one scan of your last 24 hours of inbox mail.</p>
      </div>

      <div className="bg-white border border-[#E8E6DF] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F9F8F6] border-b border-[#E8E6DF] text-xs uppercase tracking-[0.15em] text-[#8B9891]">
              <th className="text-left px-4 py-3 font-medium">Started</th>
              <th className="text-left px-4 py-3 font-medium">Trigger</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-right px-4 py-3 font-medium">Scanned</th>
              <th className="text-right px-4 py-3 font-medium">Logged</th>
              <th className="text-right px-4 py-3 font-medium">Skipped</th>
              <th className="text-right px-4 py-3 font-medium">Errors</th>
              <th className="text-right px-4 py-3 font-medium">Duration</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td className="p-6 text-[#8B9891]" colSpan={8}>Loading…</td></tr>}
            {!loading && runs.length === 0 && (
              <tr><td className="p-8 text-[#8B9891]" colSpan={8}>No scans yet. Trigger one from the dashboard.</td></tr>
            )}
            {runs.map((r) => (
              <tr data-testid={RUNS.row(r.run_id)} key={r.run_id} className="border-b last:border-0 border-[#E8E6DF] hover:bg-[#F9F8F6]">
                <td className="px-4 py-3 whitespace-nowrap text-[#1C2321]">{fmt(r.started_at)}</td>
                <td className="px-4 py-3 text-[#5D6D63] capitalize">{r.trigger}</td>
                <td className="px-4 py-3">
                  {r.status === "success" && (
                    <Badge className="rounded-full bg-[#e7f5fb] text-[#26abe2] border-transparent"><CheckCircle2 className="h-3 w-3 mr-1"/> Success</Badge>
                  )}
                  {r.status === "error" && (
                    <Badge className="rounded-full bg-[#F9EAEA] text-[#E07A5F] border-transparent"><AlertTriangle className="h-3 w-3 mr-1"/> Error</Badge>
                  )}
                  {r.status === "running" && (
                    <Badge className="rounded-full bg-[#F3F1EC] text-[#5D6D63] border-transparent"><Loader2 className="h-3 w-3 mr-1 animate-spin"/> Running</Badge>
                  )}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-[#1C2321]">{r.scanned}</td>
                <td className="px-4 py-3 text-right tabular-nums text-[#971a8c] font-medium">{r.logged}</td>
                <td className="px-4 py-3 text-right tabular-nums text-[#5D6D63]">{(r.skipped_non_parent || 0) + (r.skipped_duplicate || 0)}</td>
                <td className="px-4 py-3 text-right tabular-nums text-[#E07A5F]">{r.errors}</td>
                <td className="px-4 py-3 text-right tabular-nums text-[#5D6D63]">{dur(r.started_at, r.finished_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
